import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { SnackbarContext } from '../../../providers/SnackbarContext'
import data from '@emoji-mart/data'
import Picker from '@emoji-mart/react'
import {
  Button,
  ClickAwayListener,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Popper,
  Stack,
} from '@mui/material'
import Loading from '../../../components/Loading/Loading.component'
import DetailInput from '../../../components/DetailInput/DetailInput.component'
import { useAppNavigate } from '../../../utils/url'
import { ObstructionType } from '../../../models/ObstructionType.model'
import { ObstructionTypeAddEvent } from '../../../events/ObstructionTypeAddEvent'
import { ObstructionTypeUpdateEvent } from '../../../events/ObstructionTypeUpdateEvent'
import { ObstructionTypeService } from '../../../services/ObstructionTypeService/ObstructionTypeService'
import { AppStateContext } from '../../../providers/AppStateContext'
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon'
import SectionedForm, { FormSection } from '../../../components/Sections/SectionedForm.component'
import { ObstructionTypeSectionIds, obstructionTypeSections } from './ObstructionTypeSections'

export type ObstructionTypeFormProps = {
  obstructionType?: ObstructionType
  onAdd?: (obstructionType: ObstructionType) => void
  onEdit?: (obstructionType: ObstructionType) => void
  onCancel: () => void
  // used to remotely trigger the save action
  saveTrigger?: number
  disableRedirect?: boolean
  // section of the form to scroll to when it opens (see ObstructionTypeSectionIds)
  initialSection?: string
}

interface EmojiObject {
  native: string
}

const ObstructionTypeForm = (props: ObstructionTypeFormProps) => {
  const { obstructionType, onCancel, onAdd, onEdit, saveTrigger, disableRedirect = false, initialSection } = props
  const pickerRef = useRef<HTMLDivElement>(null)
  const navigate = useAppNavigate()
  const [tempObstructionType, setTempObstructionType] = useState<Partial<ObstructionType>>(
    {
      active: true,
      symbol: '',
      ...obstructionType,
    } || {},
  )

  const [isSubmitting, setIsSubmitting] = useState(false)

  const queryClient = useQueryClient()

  const obstructionTypeService = useMemo(() => new ObstructionTypeService(), []) // memo to prevent re-creating service on every render
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popper' : undefined
  const addObstructionType = useMutation(
    (mutationParams: { obstructionType: ObstructionTypeAddEvent }) => {
      setIsSubmitting(true)
      return obstructionTypeService.add(mutationParams.obstructionType)
    },
    {
      onSuccess: (result) => {
        SnackbarContext.show('Obstruction type added successfully!')

        if (!disableRedirect) {
          // redirect to the new ObstructionType
          navigate(`/obstruction-type/${result.id}`)
          // remove the search query to prevent auto refresh of the search results in case we are on that page and redirecting
          queryClient.removeQueries(['ObstructionTypeService.search'])
        }
        // invalidate all other queries to ensure any queries that are using the modified record are updated
        queryClient.invalidateQueries()
        setIsSubmitting(false)

        if (typeof onAdd === 'function') {
          onAdd(result)
        }
      },
      onError: (err) => {
        SnackbarContext.show(`Obstruction type failed to add: ${err}`, 'error')
        console.error(err)
        setIsSubmitting(false)
      },
    },
  )

  const updateObstructionType = useMutation(
    (mutationParams: { id: number; name: string; changedProperties: ObstructionTypeUpdateEvent }) => {
      setIsSubmitting(true)
      mutationParams.changedProperties = { ...mutationParams.changedProperties, name: mutationParams.name }
      return obstructionTypeService.update(mutationParams.id, mutationParams.changedProperties)
    },
    {
      onSuccess: (result) => {
        SnackbarContext.show('Obstruction type updated successfully!')

        // invalidate all queries to ensure any queries that are using the modified record are updated
        queryClient.invalidateQueries()
        setIsSubmitting(false)

        if (typeof onEdit === 'function') {
          onEdit(result)
        }
      },
      onError: (err) => {
        SnackbarContext.show(`Obstruction type failed to update: ${err}`, 'error')
        console.error(err)
        setIsSubmitting(false)
      },
    },
  )

  const handleSubmit = () => {
    if (!tempObstructionType.name) {
      SnackbarContext.show(`Obstruction type's name required`, 'error')
      return
    }
    if (!tempObstructionType.symbol) {
      SnackbarContext.show(`Obstruction type's symbol required`, 'error')
      return
    }
    if (tempObstructionType.sortWeight === undefined) {
      SnackbarContext.show(`Obstruction type's sort weight required `, 'error')
      return
    }
    setIsSubmitting(true)
    if (!obstructionType) {
      // format the data to match the API
      const formattedObstructionType: ObstructionTypeAddEvent = {
        name: tempObstructionType.name,
        symbol: tempObstructionType.symbol,
        sortWeight: Number(tempObstructionType.sortWeight),
        active: tempObstructionType.active,
        region: AppStateContext.getRegion(),
      }
      // adding a new record
      addObstructionType.mutate({ obstructionType: formattedObstructionType })
    } else {
      // identify the properties that have changed
      const didChange = (field: keyof ObstructionType) => {
        return tempObstructionType[field] !== undefined && tempObstructionType[field] !== obstructionType[field]
      }

      const changedProperties: ObstructionTypeUpdateEvent = {
        id: obstructionType.id,
        name: didChange('name') ? tempObstructionType.name : undefined,
        symbol: didChange('symbol') ? tempObstructionType.symbol : undefined,
        sortWeight: didChange('sortWeight') ? Number(tempObstructionType.sortWeight) : undefined,
        active: didChange('active') ? tempObstructionType.active : undefined,
      }

      // edit an existing record
      if (!Object.keys(changedProperties).filter((key) => key !== 'id').length) {
        // nothing changed so just close the dialog
        onCancel()
      } else {
        updateObstructionType.mutate({ id: obstructionType.id, name: obstructionType.name, changedProperties })
      }
    }
  }

  useEffect(() => {
    if (saveTrigger) {
      // if outside consumer triggered save, then submit
      handleSubmit()
    }
  }, [saveTrigger])

  const handleCancel = () => {
    if (typeof onCancel === 'function') {
      onCancel()
    }
  }

  const keyDownSubmit = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit()
    }
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }
  return (
    <>
      {isSubmitting && <Loading sx={{ py: 20 }} />}
      {!isSubmitting && (
        <>
          <SectionedForm sections={obstructionTypeSections} initialSection={initialSection}>
            {/* --- General ---------------------------------------------------- */}
            <FormSection id={ObstructionTypeSectionIds.general}>
              <Stack direction={{ zero: 'column', sm: 'row' }} spacing={2.5} alignItems={{ sm: 'flex-start' }}>
                <DetailInput<ObstructionType>
                  label="Name"
                  field="name"
                  tempDetail={tempObstructionType}
                  setTempDetail={setTempObstructionType}
                  keyDownSubmit={keyDownSubmit}
                />
                <DetailInput<ObstructionType>
                  label="Sort Weight"
                  field="sortWeight"
                  helperText="Lower numbers appear first in lists."
                  tempDetail={tempObstructionType}
                  setTempDetail={setTempObstructionType}
                  keyDownSubmit={keyDownSubmit}
                />
              </Stack>
            </FormSection>

            {/* --- Symbol ----------------------------------------------------- */}
            <FormSection id={ObstructionTypeSectionIds.symbol}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Symbol</InputLabel>
                <OutlinedInput
                  id={`detail-input-symbol`}
                  label="Symbol"
                  value={tempObstructionType.symbol ?? ''}
                  type={'text'}
                  placeholder={'Select a symbol or enter a custom one'}
                  required={true}
                  onChange={(e) => {
                    setTempObstructionType((prev) => ({ ...prev, ['symbol']: e.target.value }))
                  }}
                  onKeyDown={keyDownSubmit}
                  endAdornment={
                    <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                      <InputAdornment position="end">
                        <IconButton
                          aria-describedby={id}
                          aria-label="add an emoji"
                          color="primary"
                          onClick={handleClick}
                        >
                          <InsertEmoticonIcon />
                        </IconButton>
                        <Popper
                          id={id}
                          open={open}
                          anchorEl={anchorEl}
                          placement="top-end"
                          sx={{ zIndex: 1400, maxHeight: 400 }}
                          modifiers={[{ name: 'flip', enabled: false }]}
                        >
                          <Picker
                            data={data}
                            onEmojiSelect={(emoji: EmojiObject) => {
                              setTempObstructionType((prev) => ({ ...prev, ['symbol']: emoji.native }))
                            }}
                            categories={['nature', 'activity', 'travel', 'objects', 'symbols']}
                            style={{ width: 320, maxHeight: 400, overflowY: 'auto' }}
                          />
                        </Popper>
                      </InputAdornment>
                    </ClickAwayListener>
                  }
                />
                <FormHelperText>Pick an emoji with the button or type a custom symbol.</FormHelperText>
              </FormControl>
            </FormSection>
          </SectionedForm>
          <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmit}>
              Save
            </Button>
          </Stack>
        </>
      )}
    </>
  )
}
export default ObstructionTypeForm
